import React, { useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  Platform,
  Modal,
  FlatList } from 'react-native'
import {
  ArrowLeft,
  ChevronRight,
  Check,
  X,
  Plus } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Input,
  SectionHeader,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'PurchaseOrderDetail'>
  route: RouteProp<RootStackParamList, 'PurchaseOrderDetail'>
}

interface Supplier {
  id: string
  name: string
}

interface Product {
  id: string
  title: string
  sku: string | null
  cost_price: number | null
  supplier_product_code: string | null
}

interface OrderItem {
  id?: string
  product_id: string | null
  supplier_product_code: string
  description: string
  quantity_ordered: number
  quantity_received: number
  unit_cost: number
  product?: Product
}

interface OrderData {
  supplier_id: string | null
  order_number: string
  order_date: string
  expected_delivery_date: string
  status: string
  notes: string
  shipping_cost: string
  tax_amount: string
}

export default function PurchaseOrderDetailScreen({ navigation, route }: Props) {
  const { storeId, orderId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const isNew = !orderId

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [showSupplierPicker, setShowSupplierPicker] = useState(false)
  const [showProductPicker, setShowProductPicker] = useState(false)
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null)

  const [data, setData] = useState<OrderData>({
    supplier_id: null,
    order_number: `PO-${Date.now().toString(36).toUpperCase()}`,
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    status: 'draft',
    notes: '',
    shipping_cost: '0',
    tax_amount: '0' })

  const [items, setItems] = useState<OrderItem[]>([])

  useEffect(() => {
    fetchSuppliers()
    fetchProducts()
    if (orderId) {
      fetchOrder()
    }
  }, [orderId, storeId])

  const fetchSuppliers = async () => {
    const { data } = await supabase
      .from('suppliers')
      .select('id, name')
      .eq('flea_market_id', storeId)
      .eq('is_active', true)
      .order('name')

    setSuppliers(data || [])
  }

  const fetchProducts = async () => {
    const { data } = await supabase
      .from('products')
      .select('id, title, sku, cost_price, supplier_product_code')
      .eq('flea_market_id', storeId)
      .eq('ownership_type', 'store_owned')
      .order('title')

    setProducts(data || [])
  }

  const fetchOrder = async () => {
    try {
      const { data: order, error } = await supabase
        .from('purchase_orders')
        .select('*')
        .eq('id', orderId)
        .single()

      if (error) throw error

      setData({
        supplier_id: order.supplier_id,
        order_number: order.order_number,
        order_date: order.order_date,
        expected_delivery_date: order.expected_delivery_date || '',
        status: order.status,
        notes: order.notes || '',
        shipping_cost: order.shipping_cost?.toString() || '0',
        tax_amount: order.tax_amount?.toString() || '0' })

      // Fetch items
      const { data: orderItems } = await supabase
        .from('purchase_order_items')
        .select(`
          id, product_id, supplier_product_code, description, quantity_ordered, quantity_received, unit_cost,
          product:products(id, title, sku)
        `)
        .eq('purchase_order_id', orderId)

      setItems((orderItems || []).map(item => ({
        ...item,
        product: (item.product as any) as Product | undefined })))
    } catch (error) {
      console.error('Error fetching order:', error)
      showAlert(t('error') || 'Error', 'Failed to load order')
      navigation.goBack()
    } finally {
      setLoading(false)
    }
  }

  const showAlert = (title: string, message: string, onOk?: () => void) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`)
      onOk?.()
    } else {
      Alert.alert(title, message, [{ text: 'OK', onPress: onOk }])
    }
  }

  const confirmAction = (title: string, message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') {
      if (window.confirm(message)) onConfirm()
    } else {
      Alert.alert(title, message, [
        { text: t('cancel') || 'Cancel', style: 'cancel' },
        { text: t('confirm') || 'Confirm', onPress: onConfirm },
      ])
    }
  }

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity_ordered * item.unit_cost), 0)
  }

  const calculateTotal = () => {
    const subtotal = calculateSubtotal()
    const shipping = parseFloat(data.shipping_cost) || 0
    const tax = parseFloat(data.tax_amount) || 0
    return subtotal + shipping + tax
  }

  const addItem = () => {
    setItems([...items, {
      product_id: null,
      supplier_product_code: '',
      description: '',
      quantity_ordered: 1,
      quantity_received: 0,
      unit_cost: 0 }])
    setEditingItemIndex(items.length)
  }

  const updateItem = (index: number, field: keyof OrderItem, value: any) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], [field]: value }
    setItems(newItems)
  }

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const selectProduct = (product: Product, index: number) => {
    const newItems = [...items]
    newItems[index] = {
      ...newItems[index],
      product_id: product.id,
      description: product.title,
      supplier_product_code: product.supplier_product_code || '',
      unit_cost: product.cost_price || 0,
      product: product }
    setItems(newItems)
    setShowProductPicker(false)
  }

  const saveOrder = async (newStatus?: string) => {
    if (!data.supplier_id) {
      showAlert(t('error') || 'Error', t('select_supplier') || 'Please select a supplier')
      return
    }

    if (items.length === 0) {
      showAlert(t('error') || 'Error', t('add_items') || 'Please add at least one item')
      return
    }

    setSaving(true)
    try {
      const orderData = {
        flea_market_id: storeId,
        supplier_id: data.supplier_id,
        order_number: data.order_number,
        order_date: data.order_date,
        expected_delivery_date: data.expected_delivery_date || null,
        status: newStatus || data.status,
        notes: data.notes || null,
        subtotal: calculateSubtotal(),
        shipping_cost: parseFloat(data.shipping_cost) || 0,
        tax_amount: parseFloat(data.tax_amount) || 0,
        total_amount: calculateTotal(),
        created_by: user?.id }

      let savedOrderId = orderId

      if (isNew) {
        const { data: newOrder, error } = await supabase
          .from('purchase_orders')
          .insert(orderData)
          .select()
          .single()

        if (error) throw error
        savedOrderId = newOrder.id
      } else {
        const { error } = await supabase
          .from('purchase_orders')
          .update(orderData)
          .eq('id', orderId)

        if (error) throw error

        // Delete existing items
        await supabase
          .from('purchase_order_items')
          .delete()
          .eq('purchase_order_id', orderId)
      }

      // Insert items
      const itemsData = items.map(item => ({
        purchase_order_id: savedOrderId,
        product_id: item.product_id,
        supplier_product_code: item.supplier_product_code || null,
        description: item.description,
        quantity_ordered: item.quantity_ordered,
        quantity_received: item.quantity_received,
        unit_cost: item.unit_cost }))

      const { error: itemsError } = await supabase
        .from('purchase_order_items')
        .insert(itemsData)

      if (itemsError) throw itemsError

      showAlert(t('success') || 'Success', t('order_saved') || 'Order saved', () => navigation.goBack())
    } catch (error: any) {
      console.error('Save error:', error)
      showAlert(t('error') || 'Error', error.message)
    } finally {
      setSaving(false)
    }
  }

  const receiveOrder = async () => {
    if (!orderId || !user) return

    confirmAction(
      t('receive_order') || 'Receive Order',
      t('receive_order_confirm') || 'This will add all items to inventory. Continue?',
      async () => {
        setSaving(true)
        try {
          const { error } = await supabase.rpc('receive_purchase_order', {
            p_order_id: orderId,
            p_user_id: user.id })

          if (error) throw error

          showAlert(t('success') || 'Success', t('order_received') || 'Order received and stock updated', () => navigation.goBack())
        } catch (error: any) {
          console.error('Receive error:', error)
          showAlert(t('error') || 'Error', error.message)
        } finally {
          setSaving(false)
        }
      }
    )
  }

  const getSupplierName = (id: string | null) => {
    if (!id) return t('select_supplier') || 'Select supplier'
    const supplier = suppliers.find(s => s.id === id)
    return supplier?.name || t('select_supplier') || 'Select supplier'
  }

  const formatCurrency = (amount: number) => `€${amount.toFixed(2)}`

  const renderSupplierPicker = () => (
    <Modal visible={showSupplierPicker} animationType="slide" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('select_supplier') || 'Select Supplier'}</Text>
            <TouchableOpacity onPress={() => setShowSupplierPicker(false)} style={styles.modalCloseButton}>
              <X size={24} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={suppliers}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[styles.pickerItem, data.supplier_id === item.id && styles.pickerItemSelected]}
                onPress={() => {
                  setData(prev => ({ ...prev, supplier_id: item.id }))
                  setShowSupplierPicker(false)
                }}
              >
                <Text style={styles.pickerItemText}>{item.name}</Text>
                {data.supplier_id === item.id && <Check size={18} color={merchantColors.primary} />}
              </TouchableOpacity>
            )}
            ListEmptyComponent={
              <View style={styles.emptyPicker}>
                <Text style={styles.emptyPickerText}>{t('no_suppliers') || 'No suppliers'}</Text>
                <TouchableOpacity
                  style={styles.emptyPickerButton}
                  onPress={() => {
                    setShowSupplierPicker(false)
                    navigation.navigate('SupplierManagement', { storeId })
                  }}
                >
                  <Text style={styles.emptyPickerButtonText}>{t('add_supplier') || 'Add Supplier'}</Text>
                </TouchableOpacity>
              </View>
            }
          />
        </View>
      </View>
    </Modal>
  )

  const renderProductPicker = () => (
    <Modal visible={showProductPicker} animationType="slide" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('select_product') || 'Select Product'}</Text>
            <TouchableOpacity onPress={() => setShowProductPicker(false)} style={styles.modalCloseButton}>
              <X size={24} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={products}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.pickerItem}
                onPress={() => editingItemIndex !== null && selectProduct(item, editingItemIndex)}
              >
                <View style={styles.productPickerInfo}>
                  <Text style={styles.pickerItemText}>{item.title}</Text>
                  {item.sku && <Text style={styles.productPickerSku}>SKU: {item.sku}</Text>}
                </View>
                {item.cost_price && (
                  <Text style={styles.productPickerPrice}>{formatCurrency(item.cost_price)}</Text>
                )}
              </TouchableOpacity>
            )}
          />
        </View>
      </View>
    </Modal>
  )

  const renderItem = (item: OrderItem, index: number) => (
    <View key={index} style={styles.itemCard}>
      <View style={styles.itemHeader}>
        <TouchableOpacity
          style={styles.productSelector}
          onPress={() => {
            setEditingItemIndex(index)
            setShowProductPicker(true)
          }}
        >
          <Text style={[styles.productName, !item.product_id && styles.productNamePlaceholder]}>
            {item.description || (t('select_product') || 'Select product')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.removeItemButton} onPress={() => removeItem(index)}>
          <X size={16} color="#F44336" />
        </TouchableOpacity>
      </View>

      <View style={styles.itemDetails}>
        <View style={styles.itemField}>
          <Text style={styles.itemLabel}>{t('qty') || 'Qty'}</Text>
          <TextInput
            style={styles.itemInput}
            value={item.quantity_ordered.toString()}
            onChangeText={v => updateItem(index, 'quantity_ordered', parseInt(v) || 0)}
            keyboardType="number-pad"
          />
        </View>
        <View style={styles.itemField}>
          <Text style={styles.itemLabel}>{t('unit_cost') || 'Unit Cost'}</Text>
          <TextInput
            style={styles.itemInput}
            value={item.unit_cost.toString()}
            onChangeText={v => updateItem(index, 'unit_cost', parseFloat(v) || 0)}
            keyboardType="decimal-pad"
          />
        </View>
        <View style={styles.itemField}>
          <Text style={styles.itemLabel}>{t('line_total') || 'Total'}</Text>
          <Text style={styles.lineTotal}>{formatCurrency(item.quantity_ordered * item.unit_cost)}</Text>
        </View>
      </View>
    </View>
  )

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  const canEdit = data.status === 'draft'
  const canReceive = data.status === 'shipped' || data.status === 'confirmed'

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButtonContainer}>
          <ArrowLeft size={18} color={merchantColors.primary} />
          <Text style={styles.backButton}>{t('cancel') || 'Cancel'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>
          {isNew ? (t('new_order') || 'New Order') : data.order_number}
        </Text>
        <View style={{ width: 60 }} />
      </View>

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {/* Status badge */}
        {!isNew && (
          <View style={styles.statusSection}>
            <Text style={styles.statusText}>
              {t('status') || 'Status'}: <Text style={styles.statusValue}>{data.status}</Text>
            </Text>
          </View>
        )}

        {/* Order info */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('order_info') || 'Order Information'}</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('supplier') || 'Supplier'} *</Text>
            <TouchableOpacity
              style={[styles.picker, !canEdit && styles.pickerDisabled]}
              onPress={() => canEdit && setShowSupplierPicker(true)}
              disabled={!canEdit}
            >
              <Text style={[styles.pickerText, !data.supplier_id && styles.pickerPlaceholder]}>
                {getSupplierName(data.supplier_id)}
              </Text>
              {canEdit && <ChevronRight size={16} color={theme.colors.text.secondary} />}
            </TouchableOpacity>
          </View>

          <View style={styles.row}>
            <View style={[styles.inputGroup, { flex: 1 }]}>
              <Text style={styles.label}>{t('order_date') || 'Order Date'}</Text>
              <TextInput
                style={[styles.input, !canEdit && styles.inputDisabled]}
                value={data.order_date}
                onChangeText={v => setData(prev => ({ ...prev, order_date: v }))}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={theme.colors.text.secondary}
                editable={canEdit}
              />
            </View>
            <View style={[styles.inputGroup, { flex: 1, marginLeft: 12 }]}>
              <Text style={styles.label}>{t('expected_delivery') || 'Expected Delivery'}</Text>
              <TextInput
                style={[styles.input, !canEdit && styles.inputDisabled]}
                value={data.expected_delivery_date}
                onChangeText={v => setData(prev => ({ ...prev, expected_delivery_date: v }))}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={theme.colors.text.secondary}
                editable={canEdit}
              />
            </View>
          </View>
        </View>

        {/* Items */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>{t('items') || 'Items'}</Text>
            {canEdit && (
              <TouchableOpacity style={styles.addItemButton} onPress={addItem}>
                <Plus size={14} color="#fff" />
                <Text style={styles.addItemText}>{t('add_item') || 'Add Item'}</Text>
              </TouchableOpacity>
            )}
          </View>

          {items.map((item, index) => renderItem(item, index))}

          {items.length === 0 && (
            <View style={styles.emptyItems}>
              <Text style={styles.emptyItemsText}>{t('no_items') || 'No items added'}</Text>
            </View>
          )}
        </View>

        {/* Totals */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('totals') || 'Totals'}</Text>

          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>{t('subtotal') || 'Subtotal'}</Text>
            <Text style={styles.totalsValue}>{formatCurrency(calculateSubtotal())}</Text>
          </View>

          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>{t('shipping') || 'Shipping'}</Text>
            <TextInput
              style={[styles.totalsInput, !canEdit && styles.inputDisabled]}
              value={data.shipping_cost}
              onChangeText={v => setData(prev => ({ ...prev, shipping_cost: v }))}
              keyboardType="decimal-pad"
              editable={canEdit}
            />
          </View>

          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>{t('tax') || 'Tax'}</Text>
            <TextInput
              style={[styles.totalsInput, !canEdit && styles.inputDisabled]}
              value={data.tax_amount}
              onChangeText={v => setData(prev => ({ ...prev, tax_amount: v }))}
              keyboardType="decimal-pad"
              editable={canEdit}
            />
          </View>

          <View style={[styles.totalsRow, styles.totalsFinal]}>
            <Text style={styles.totalsFinalLabel}>{t('total') || 'Total'}</Text>
            <Text style={styles.totalsFinalValue}>{formatCurrency(calculateTotal())}</Text>
          </View>
        </View>

        {/* Notes */}
        <View style={styles.section}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('notes') || 'Notes'}</Text>
            <TextInput
              style={[styles.input, styles.textArea, !canEdit && styles.inputDisabled]}
              value={data.notes}
              onChangeText={v => setData(prev => ({ ...prev, notes: v }))}
              placeholder={t('order_notes_placeholder') || 'Any notes for this order...'}
              placeholderTextColor={theme.colors.text.secondary}
              multiline
              numberOfLines={3}
              editable={canEdit}
            />
          </View>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          {canEdit && (
            <>
              <TouchableOpacity
                style={[styles.primaryButton, saving && styles.buttonDisabled]}
                onPress={() => saveOrder('sent')}
                disabled={saving}
              >
                {saving ? (
                  <Spinner color="#fff" />
                ) : (
                  <Text style={styles.primaryButtonText}>{t('save_send') || 'Save & Send'}</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.secondaryButton, saving && styles.buttonDisabled]}
                onPress={() => saveOrder()}
                disabled={saving}
              >
                <Text style={styles.secondaryButtonText}>{t('save_draft') || 'Save Draft'}</Text>
              </TouchableOpacity>
            </>
          )}

          {canReceive && (
            <TouchableOpacity
              style={[styles.receiveButton, saving && styles.buttonDisabled]}
              onPress={receiveOrder}
              disabled={saving}
            >
              {saving ? (
                <Spinner color="#fff" />
              ) : (
                <Text style={styles.receiveButtonText}>{t('receive_order') || 'Receive Order'}</Text>
              )}
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.bottomSpacer} />
      </ScrollView>

      {renderSupplierPicker()}
      {renderProductPicker()}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  backButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center' },
  backButton: {
    color: merchantColors.primary,
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 4 },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  content: {
    flex: 1,
    padding: theme.spacing.md },
  statusSection: {
    backgroundColor: '#E3F2FD',
    padding: 12,
    borderRadius: theme.borderRadius.md,
    marginBottom: 16 },
  statusText: {
    fontSize: 14,
    color: '#1976D2' },
  statusValue: {
    fontWeight: 'bold',
    textTransform: 'capitalize' },
  section: {
    marginBottom: 24 },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12 },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 12 },
  inputGroup: {
    marginBottom: 16 },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  input: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    fontSize: 16,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  inputDisabled: {
    backgroundColor: theme.colors.background.secondary,
    color: theme.colors.text.secondary },
  textArea: {
    height: 80,
    textAlignVertical: 'top' },
  row: {
    flexDirection: 'row' },
  picker: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  pickerDisabled: {
    backgroundColor: theme.colors.background.secondary },
  pickerText: {
    fontSize: 16,
    color: theme.colors.text.primary },
  pickerPlaceholder: {
    color: theme.colors.text.secondary },
  addItemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: merchantColors.primary,
    borderRadius: theme.borderRadius.sm,
    gap: 4 },
  addItemText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600' },
  itemCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12 },
  productSelector: {
    flex: 1 },
  productName: {
    fontSize: 15,
    fontWeight: '500',
    color: theme.colors.text.primary },
  productNamePlaceholder: {
    color: theme.colors.text.secondary },
  removeItemButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#FFEBEE',
    justifyContent: 'center',
    alignItems: 'center' },
  itemDetails: {
    flexDirection: 'row',
    gap: 12 },
  itemField: {
    flex: 1 },
  itemLabel: {
    fontSize: 11,
    color: theme.colors.text.secondary,
    marginBottom: 4 },
  itemInput: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.sm,
    padding: 10,
    fontSize: 14,
    textAlign: 'center',
    color: theme.colors.text.primary },
  lineTotal: {
    fontSize: 14,
    fontWeight: '600',
    color: merchantColors.primary,
    textAlign: 'center',
    paddingVertical: 10 },
  emptyItems: {
    padding: 24,
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md },
  emptyItemsText: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8 },
  totalsLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  totalsValue: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  totalsInput: {
    width: 100,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.sm,
    padding: 8,
    fontSize: 14,
    textAlign: 'right',
    borderWidth: 1,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  totalsFinal: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border,
    marginTop: 8,
    paddingTop: 12 },
  totalsFinalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  totalsFinalValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: merchantColors.primary },
  actions: {
    gap: 12,
    marginTop: 16 },
  primaryButton: {
    backgroundColor: merchantColors.primary,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center' },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  secondaryButton: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: merchantColors.primary },
  secondaryButtonText: {
    color: merchantColors.primary,
    fontSize: 16,
    fontWeight: '600' },
  receiveButton: {
    backgroundColor: '#4CAF50',
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center' },
  receiveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  buttonDisabled: {
    opacity: 0.6 },
  bottomSpacer: {
    height: 120 },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '70%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  modalCloseButton: {
    padding: 4 },
  pickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  pickerItemSelected: {
    backgroundColor: `${merchantColors.primary}10` },
  pickerItemText: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text.primary },
  productPickerInfo: {
    flex: 1 },
  productPickerSku: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  productPickerPrice: {
    fontSize: 14,
    fontWeight: '600',
    color: merchantColors.primary },
  emptyPicker: {
    padding: theme.spacing.xl,
    alignItems: 'center' },
  emptyPickerText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.md },
  emptyPickerButton: {
    backgroundColor: merchantColors.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: theme.borderRadius.md },
  emptyPickerButtonText: {
    color: '#fff',
    fontWeight: '600' } })
